import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { FundService, FundValues } from 'src/app/services/fund/fund.service';
import DDIOptions from 'src/app/data/ddi';
import Utils, { UtilsValidators } from 'src/app/helpers/utils';
import UIDefaultTheme from 'src/themes';
import { SignupService } from 'src/app/services/signup/signup.service';
import fakerbr from 'faker-br';
import { MaskPipe } from 'ngx-mask';
import { distinctUntilChanged } from 'rxjs/operators';
import { ToastService } from 'src/app/services/toast/toast.service';
import { ApiService } from 'src/app/services/api/api.service';

@Component({
  selector: 'app-dashboard-fund-approval-register-basic',
  templateUrl: './dashboard-fund-approval-register-basic.component.html',
  styleUrls: ['./dashboard-fund-approval-register-basic.component.scss'],
})
export class DashboardFundApprovalRegisterBasicComponent implements OnInit {
  @Input() loading: boolean = true;
  @Input() isEditing: boolean = false;

  DDIOptions = DDIOptions;

  form = this.formBuilder.group({
    document: [null, [Validators.required, UtilsValidators.cnpj]],
    giin: [null, []],
    cbm_constitution_date: [
      null,
      [Validators.required, Utils.validateMinDate, Utils.validateMaxMinToday],
    ],
    name: [null, [Validators.required]],
    fantasy_name: [null, [Validators.required]],
    structuring_fee_date: [
      null,
      [Validators.required, Utils.validateMinDate, Utils.validateMaxDate],
    ],
    duration_period: [
      null,
      [Validators.required, Utils.validateMinDate, Utils.validateMaxDate],
    ],
    indefinite_valid_date: [null, []],
    cvm_classification: [null, [Validators.required]],
    anbima_classification: [null, [Validators.required]],
    profile: [null, [Validators.required]],
    target_audience: [null, [Validators.required]],
    fund_social_exercise: [null, [Validators.required]],
    condominium_type: [null, [Validators.required]],
    financial_assets: [null, [Validators.required]],
    transfer: [false, []],
    transfer_date: [null, []],
    assembly_date: [null, []],
    last_company_document: [null, []],
    last_company_name: [null, []],
    last_company_contact: [null, []],
    last_company_email: [null, []],
    last_company_telephone_ddi: ['55', []],
    last_company_telephone_number: [null, []],
    last_company_cellphone_ddi: ['55', []],
    last_company_cellphone_number: [null, []],
  });

  fund: FundValues = undefined;

  choices: { [Key in any]: any } = {};
  today = Utils.todayString();
  canEdit = false;
  last_fund_document: string;
  last_fund_giin: string;
  UIDefaultTheme = UIDefaultTheme;

  constructor(
    public fundService: FundService,
    private formBuilder: FormBuilder,
    private signupService: SignupService,
    private maskPipe: MaskPipe,
    private toastService: ToastService,
    private api: ApiService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.loading.previousValue !== changes.loading.currentValue) {
      this.setData(this.fundService.data);
      this.choices = this.fundService.choices;
    }
  }

  ngOnInit(): void {
    this.form.controls.document.valueChanges
      .pipe(distinctUntilChanged((a: any, b: any) => a === b))
      .subscribe((value) => {
        if (this.isEditing) {
          // Só compara quando o fundo já foi carregado (last_fund_document definido em setData)
          if (!this.last_fund_document && this.fundService.data?.document) {
            this.last_fund_document = Utils.onlyNumbers(
              this.fundService.data.document,
            );
          }
          if (this.last_fund_document == null) {
            return;
          }

          if (this.last_fund_document !== Utils.onlyNumbers(value)) {
            if (value && value.length === 18) {
              this.checkDocument(value, 'document');
            }
          } else {
            this.canEdit = true;
            this.fundService.canEdit.emit(true);
            return;
          }
        } else {
          if (value && value.length === 18) {
            this.checkDocument(value, 'document');
          }
        }
      });

    this.form.controls.giin.valueChanges
      .pipe(distinctUntilChanged((a: any, b: any) => a === b))
      .subscribe((value) => {
        if (this.isEditing) {
          if (!this.last_fund_giin) {
            if (this.fundService.data.giin) {
              this.last_fund_giin = this.fundService.data.giin;
            }
          }
          if (this.last_fund_giin !== value) {
            if (value && value.length >= 3) {
              this.checkDocument(value, 'giin');
            }
          }
        } else {
          if (value && value.length >= 3) {
            this.checkDocument(value, 'giin');
          }
        }
      });

    // Listener para atualizar validações quando a data de constituição mudar
    this.form.controls.cbm_constitution_date.valueChanges
      .pipe(distinctUntilChanged((a: any, b: any) => a === b))
      .subscribe((constitutionDate) => {
        // Salvar no service para outros componentes acessarem
        this.fundService.setConstitutionDate(constitutionDate);

        if (constitutionDate) {
          // Atualizar validação de structuring_fee_date
          const structuringValidators = this.UIDefaultTheme.brand.simplifiedFund
            ? [
                Utils.validateWhenNotEmpty(Utils.validateMinDate),
                Utils.validateWhenNotEmpty(Utils.validateMaxDate),
                Utils.validateWhenNotEmpty(
                  Utils.validateMinConstitutionDate(constitutionDate),
                ),
              ]
            : [
                Validators.required,
                Utils.validateMinDate,
                Utils.validateMaxDate,
                Utils.validateMinConstitutionDate(constitutionDate),
              ];

          this.form.controls.structuring_fee_date.setValidators(
            structuringValidators,
          );
          this.form.controls.structuring_fee_date.updateValueAndValidity();

          // Atualizar validação de duration_period (se não for indeterminado)
          if (!this.form.controls.indefinite_valid_date.value) {
            const durationValidators = this.UIDefaultTheme.brand.simplifiedFund
              ? [
                  Utils.validateWhenNotEmpty(Utils.validateMinDate),
                  Utils.validateWhenNotEmpty(Utils.validateMaxDate),
                  Utils.validateWhenNotEmpty(
                    Utils.validateMinConstitutionDate(constitutionDate),
                  ),
                ]
              : [
                  Validators.required,
                  Utils.validateMinDate,
                  Utils.validateMaxDate,
                  Utils.validateMinConstitutionDate(constitutionDate),
                ];

            this.form.controls.duration_period.setValidators(
              durationValidators,
            );
            this.form.controls.duration_period.updateValueAndValidity();
          }
        }
      });

    if (this.UIDefaultTheme.brand.simplifiedFund) {
      this.form.controls.cbm_constitution_date.setValidators([
        Utils.validateWhenNotEmpty(Utils.validateMinDate),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ]);
      this.form.controls.structuring_fee_date.setValidators([
        Utils.validateWhenNotEmpty(Utils.validateMinDate),
        Utils.validateWhenNotEmpty(Utils.validateMaxDate),
      ]);
      this.form.controls.duration_period.setValidators([
        Utils.validateWhenNotEmpty(Utils.validateMinDate),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ]);
      this.form.controls.profile.setValidators([]);
      this.form.controls.target_audience.setValidators([]);
      this.form.controls.fund_social_exercise.setValidators([]);
      this.form.controls.condominium_type.setValidators([]);
      this.form.controls.financial_assets.setValidators([]);
      this.form.updateValueAndValidity();
    }
    this.form.controls.indefinite_valid_date.valueChanges.subscribe(
      (values) => {
        const constitutionDate = this.form.controls.cbm_constitution_date.value;

        if (values) {
          this.form.controls.duration_period.setValue(null);
          this.form.controls.duration_period.setValidators([]);
        } else {
          const validators = !this.UIDefaultTheme.brand.simplifiedFund
            ? [
                Validators.required,
                Utils.validateMinDate,
                Utils.validateMaxDate,
                ...(constitutionDate
                  ? [Utils.validateMinConstitutionDate(constitutionDate)]
                  : []),
              ]
            : [
                Utils.validateWhenNotEmpty(Utils.validateMinDate),
                Utils.validateWhenNotEmpty(Utils.validateMaxDate),
                ...(constitutionDate
                  ? [
                      Utils.validateWhenNotEmpty(
                        Utils.validateMinConstitutionDate(constitutionDate),
                      ),
                    ]
                  : []),
              ];
          this.form.controls.duration_period.setValidators(validators);
        }
        this.form.controls.duration_period.updateValueAndValidity();
      },
    );
    this.form.valueChanges.subscribe((values) => {
      const {
        transfer,
        transfer_date,
        assembly_date,
        last_company_document,
        last_company_name,
        last_company_contact,
        last_company_email,
        last_company_telephone_ddi,
        last_company_telephone_number,
        last_company_cellphone_ddi,
        last_company_cellphone_number,
        ...basicValues
      } = values;
      const payload: Partial<FundValues> = {
        ...basicValues,
        cvm_classification: Number(values.cvm_classification),
        anbima_classification: Number(values.anbima_classification),
        target_audience: Number(values.target_audience),
        financial_assets: Number(values.financial_assets),
        condominium_type: Number(values.condominium_type),
        fund_social_exercise: Number(values.fund_social_exercise),
        client_service: {
          ...this.fundService?.data?.client_service,
        },
        fund_current_profile: values.profile ? Number(values.profile) : null,
      };
      this.fundService.setFundFormData({
        ...this.fundService.data,
        ...payload,
      });
    });
    this.form.statusChanges.subscribe((status) =>
      this.fundService.setFundFormStatus('basic', status === 'VALID'),
    );
    this.setRandomData();
  }

  async checkDocument(number: string, type: string) {
    try {
      const res = await this.api.post({
        route: 'api/registration/check_fund/',
        token: true,
        body: { number: number, type: type },
      });

      this.canEdit = true;
      this.fundService.canEdit.emit(true);
    } catch (error) {
      console.log(error);
      this.toastService.show('error', 'Erro', error.error);
      this.form.controls.document.setErrors({ alreadyExists: true });
      this.fundService.canEdit.emit(false);
    }
  }

  setRandomData() {
    this.signupService.fillFormEvent.subscribe(() => {
      const companyName = fakerbr.company.companyName();

      const eventType = fakerbr.random.number({ min: 1, max: 2 });

      const isTransfer = eventType === 2;

      this.form.patchValue({
        transfer: isTransfer,
        profile: String(fakerbr.random.number({ min: 1, max: 3 })),
        target_audience: String(fakerbr.random.number({ min: 1, max: 5 })),
        fund_social_exercise: String(
          fakerbr.random.number({ min: 1, max: 12 }),
        ),
        transfer_date: isTransfer
          ? fakerbr.date.past(20).toLocaleDateString('pt-BR', {
              timeZone: 'UTC',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })
          : null,
        assembly_date: isTransfer
          ? fakerbr.date.past(20).toLocaleDateString('pt-BR', {
              timeZone: 'UTC',
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })
          : null,
        structuring_fee_date: fakerbr.date
          .past(20)
          .toLocaleDateString('pt-BR', {
            timeZone: 'UTC',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
        duration_period: fakerbr.date.past(20).toLocaleDateString('pt-BR', {
          timeZone: 'UTC',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
        cbm_constitution_date: fakerbr.date
          .past(20)
          .toLocaleDateString('pt-BR', {
            timeZone: 'UTC',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
        last_company_name: isTransfer ? fakerbr.company.companyName() : null,
        last_company_document: isTransfer
          ? Utils.formatDocumentForDisplay(fakerbr.br.cnpj())
          : null,
        last_company_contact: isTransfer ? fakerbr.name.findName() : null,
        last_company_email: isTransfer ? fakerbr.internet.email() : null,
        last_company_telephone_ddi: isTransfer ? '55' : null,
        last_company_telephone_number: isTransfer
          ? this.maskPipe.transform(
              '139' +
                String(fakerbr.random.number({ min: 1111111, max: 9999999 })),
              '(00) 0000-0000',
            )
          : null,
        last_company_cellphone_ddi: isTransfer ? '55' : null,
        last_company_cellphone_number: isTransfer
          ? this.maskPipe.transform(
              '139' +
                String(fakerbr.random.number({ min: 11111111, max: 99999999 })),
              '(00) 00000-0000',
            )
          : null,
        name: companyName,
        fantasy_name: companyName,
        giin: fakerbr.random.number({ min: 100000, max: 99999999 }),
        document: Utils.formatDocumentForDisplay(fakerbr.br.cnpj()),
        condominium_type: String(fakerbr.random.number({ min: 1, max: 2 })),
        financial_assets: String(fakerbr.random.number({ min: 1, max: 3 })),
        anbima_classification: String(
          fakerbr.random.number({ min: 1, max: 64 }),
        ),
        cvm_classification: String(fakerbr.random.number({ min: 1, max: 25 })),
        constitution_date: fakerbr.date.past(20).toLocaleDateString('pt-BR', {
          timeZone: 'UTC',
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
      });
    });
  }

  getChoicesName(object: string, value: any) {
    const choices = this.fundService.choices;

    const choiceObject = choices[object];

    if (choiceObject) {
      return choiceObject.find((_choice: any) => _choice.value == value);
    }

    return { label: '', value: '' };
  }

  setData(values: FundValues) {
    this.fund = values;

    if (values) {
      // Garantir last_fund_document/giin antes do patchValue para o valueChanges não rodar sem eles
      if (values.document) {
        this.last_fund_document = Utils.onlyNumbers(values.document);
      }
      if (values.giin) {
        this.last_fund_giin = values.giin;
      }

      const constitutionDateFormatted = values.cbm_constitution_date
        ? values.cbm_constitution_date.split('-').reverse().join('/')
        : null;

      // Salvar a data de constituição no service
      if (constitutionDateFormatted) {
        this.fundService.setConstitutionDate(constitutionDateFormatted);
      }

      this.form.patchValue({
        ...values,
        document: this.formatDocument(values.document),
        structuring_fee_date: values.structuring_fee_date
          ? values.structuring_fee_date.split('-').reverse().join('/')
          : null,

        cbm_constitution_date: constitutionDateFormatted,
        duration_period: values.duration_period
          ? values.duration_period.split('-').reverse().join('/')
          : null,
        transfer_date: values.transfer_date
          ? values.transfer_date.split('-').reverse().join('/')
          : null,
        assembly_date: values.assembly_date
          ? values.assembly_date.split('-').reverse().join('/')
          : null,
        cvm_classification: String(values.cvm_classification),
        anbima_classification: String(values.anbima_classification),
        target_audience: values.target_audience
          ? String(values.target_audience)
          : null,
        financial_assets: values.financial_assets
          ? String(values.financial_assets)
          : null,
        condominium_type: values.condominium_type
          ? String(values.condominium_type)
          : null,
        fund_social_exercise: values.fund_social_exercise
          ? String(values.fund_social_exercise)
          : null,
        profile:
          values.fund_current_profile
            ? String(values.fund_current_profile)
            : null,
        transfer: values.transfer_date ? true : false,
        indefinite_valid_date: values.duration_period ? false : true,
      });
    }

    if (this.isEditing) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
    }

    // Em edição, se o documento do form já é o do fundo carregado, liberar canEdit na hora
    if (
      this.isEditing &&
      this.last_fund_document &&
      this.form.controls.document.value &&
      Utils.onlyNumbers(this.form.controls.document.value) ===
        this.last_fund_document
    ) {
      this.canEdit = true;
      this.fundService.canEdit.emit(true);
    }

    this.fundService.setFundFormStatus('basic', this.form.valid);
  }

  formatDocument(document: any) {
    return document.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      '$1.$2.$3/$4-$5',
    );
  }

  formatDate(date: string) {
    return new Date(date).toLocaleDateString();
  }
}
